/**
 * Pillar 5 정부지원사업 — 정규 타입.
 *
 * 수집 워커(apps/api/src/ingest)와 Phase 2 읽기 API, Phase 3 웹 화면이
 * 같은 타입을 쓴다. 출처가 무엇이든 이 형태로 수렴한다.
 */
import { z } from 'zod';
import { paginationQuerySchema } from './pagination.js';
import { cleanSourceText } from './text.js';
import { REGIONS, sortRegions } from './regions.js';
import { PROGRAM_KINDS } from './grant-support.js';
import { CERTIFICATION_CODES } from './grant-codes.js';
/** 공고 생애주기. new/updated 는 순간 이벤트가 아니라 노출 전까지 유지되는 상태다. */
export const GRANT_STATUSES = ['new', 'open', 'updated', 'closed'];
/**
 * 금액 추출 상태.
 *
 * `absent` 와 `skipped`·`failed` 를 가르는 게 요점이다 — 앞의 것은 **문서를
 * 읽었고 금액이 없다**는 결론(실측 표본의 18%가 이렇다: 인증·지정·포상 공고)
 * 이고, 뒤의 둘은 아직 답을 모르는 상태다. 재시도 대상이 갈린다.
 */
export const AMOUNT_STATUSES = [
    /** 아직 시도하지 않았다. */
    'pending',
    /** 금액을 찾았다. */
    'found',
    /** 상한 없이 비율만 있다 (총사업비의 50%). */
    'ratio_only',
    /** 문서를 읽었고 금액이 없다. 정당한 "미기재"다. */
    'absent',
    /** 읽을 수 있는 첨부가 없다 (스캔 이미지뿐이거나 첨부 없음). */
    'skipped',
    /** 다운로드·추출이 실패했다. 재시도 대상. */
    'failed',
];
/**
 * 배치(텍스트 추출)의 결과. **금액 판정과 무관하다.**
 *
 * 배치는 바이트를 텍스트로 바꾸는 일만 한다. 해석은 전부 다음 단계다.
 */
export const TEXT_STATUSES = [
    /** 200 단어 이상 읽었다. 해석 대상. */
    'ok',
    /**
     * **첨부가 정말 0개다.** 기다려도 안 생긴다.
     *
     * 이 값은 첨부 배열이 비었을 때만 쓴다. 이전 판은 못 여는 형식이면 전부
     * 여기로 보냈고, 그래서 png+txt 가 붙은 공고가 화면에 「첨부 없음」으로
     * 나왔다 — 들어가 보면 첨부가 둘 다 있다. 우리는 남의 데이터를 정제해
     * 내보내는 쪽인데 우리가 거짓말을 한 셈이다.
     */
    'no_file',
    /**
     * 첨부는 있는데 **결정론적으로 열 수 있는 형식이 하나도 없다** (이미지뿐,
     * 구형 포맷). 기다려도 안 생기고, OCR 이 읽으면 생긴다.
     */
    'unreadable',
    /** 열었는데 글자가 모자란다 — 스캔 문서. 비전 모델이 읽어야 한다. */
    'no_text',
    /** 후보를 전부 시도했고 전부 터졌다 (네트워크·DRM·파서). 재시도 대상. */
    'failed',
];
/**
 * 이보다 짧으면 **글자를 못 얻은 것**으로 본다.
 *
 * 이 값의 일은 「분량이 충분한가」가 아니라 **「무언가를 읽었는가, 아무것도
 * 못 읽었는가」**를 가르는 것이다. 그래서 낮다.
 *
 * 처음엔 200이었다. 첨부 공고문 72건으로 잡은 값이고 거기선 맞았다 — 깨진
 * 문서가 0단어, 다음으로 작은 정상 문서가 229단어였다. 그런데 사다리에
 * **상세 페이지 본문과 OCR** 이 들어오면서 그 계산이 깨졌다. 행사·설명회·
 * 대관 공고는 원래 200단어가 안 된다.
 *
 * 실측한 짧은 쪽 (단어 수 순):
 *
 *   14 ocr   `(사)한국엔젤투자협회 / 동남권엔젤투자허브 / ??? / Srpriselusky`  로고뿐
 *   20 ocr   `K O R E A S T A R T U F P / S / P / TPSTOWN`                     로고뿐
 *   30 ocr   `수원대학교 / THE UNIVERSITY OF SUWO I / 중소벤처기업부 그`        로고뿐
 *   ── 여기서 갈린다 ──
 *   78 txt   강연자·일시·장소. 완결된 행사 공고다
 *   85 hwpx  판다포럼 일자·장소
 *   88 ocr   **`1년 간 월 최대 60만원, 총 720만원 지원!`** — 지원금이 여기 있다
 *  104 ocr   커리큘럼 전체
 *
 * **30과 78 사이에 아무것도 없다.** 200을 유지하면 저 720만원짜리 공고가
 * `no_text` 로 남아 해석 큐(`text_status='ok'`)에서 통째로 빠진다 — 우리가
 * 글자를 읽어 놓고 스스로 버리는 것이다.
 */
export const MIN_NOTICE_WORDS = 50;
/**
 * 이 첨부를 **본문으로 읽지 않을 것인가.**
 *
 * 여기 있는 이유: 이 판정이 두 곳에서 필요하다. 추출 배치가 「읽을까」를
 * 정할 때(`ingest/documents.ts`), 화면이 「서식」이라고 표시할 때
 * (`GrantAttachmentList`). **두 곳이 다른 기준을 쓰면 화면이 거짓말을 한다** —
 * 실측 1,888건이 화면에는 「서식」인데 실제로는 본문으로 읽혔다.
 *
 * 판정은 **이름만 본다.** `kind` 를 안 믿는 이유가 있다: 기업마당은 API 필드
 * 위치로 `kind` 를 정하는데, 공고문을 서식 필드에 넣는 일이 있어
 * `kind='form'` 인 파일이 실제로는 `(공고문) 부산 관광 마이스 3UP
 * 프로젝트.hwp` 인 경우가 실측 159건이었다. 이름이 진실이다.
 *
 * 자간 공백을 허용한다 — `서 식`·`붙 임` 처럼 띄워 쓰는 일이 흔하다.
 */
const FORM_NAME_RE = /서\s*식|양\s*식|붙\s*임|별\s*첨|신\s*청\s*서|사업계획서|동\s*의\s*서|확\s*약\s*서|서\s*약\s*서/;
/**
 * 위 규칙을 **이기는** 이름. 파일이 스스로 공고라고 말하면 읽는다. 「내용」도
 * 같다 — `[붙임2] 세부지원 내용.hwp` 는 서식이 아니라 지원 내용 그 자체다.
 *
 * 기관이 공고문을 붙임으로 매다는 관행이 흔하다. 실측(`붙임` 첨부 4,604개):
 * `공고문` 을 가진 것 791개, `공고` 까지 넓히면 564개가 더 살아난다 —
 * `(붙임 1) 인허가 지원 프로그램 모집공고.pdf` 처럼. 그래서 `문` 을 요구하지
 * 않는다.
 */
/**
 * RFP(제안요청서·과제제안요구서)도 반드시 읽는다. **예산이 거기 있다.** 연구과제는
 * 공고문이 절차만 말하고 과제별 연구비는 RFP 표에 적힌다 — 기업이 그 돈을 받고
 * 과제를 수행하는 구조라, RFP 를 안 읽으면 금액이 통째로 빠진다.
 */
const NOTICE_NAME_RE = /공\s*고|내\s*용|RFP|제안\s*요청서|과제\s*제안\s*요구서/i;
/**
 * 「지원 대상 및 내용」은 그 공고에서 **가장 먼저 읽을 문서**다. 실측 동두천시
 * (`3203ef76`): `[붙임1] 세부지원 대상 및 내용.hwpx` 가 「붙임」 때문에 서식으로
 * 걸러져 금액이 통째로 빠졌다. 이름이 곧 「무엇을 얼마나 주나」라고 말하는 파일이다.
 */
const PRIMARY_NAME_RE = /대상\s*및\s*내용/;
export function isFormName(name) {
    return FORM_NAME_RE.test(name) && !NOTICE_NAME_RE.test(name);
}
/**
 * 이름이 **스스로 공고라고 말하는가.**
 *
 * 배제 목록만으로는 부족하다. 실측 `2c462be5`(성장기업 재무·세무 바우처):
 *
 *   [개인정보이용동의서]….hwp   ← 사다리가 여기서 225단어를 얻고 멈췄다
 *   [신청서]….hwp               ← 걸러짐
 *   [모집공고문]….pdf           ← 여기까지 가지도 못했다
 *
 * 저장된 본문이 「□ 동의 / □ 거부」가 반복되는 빈 표였다. 단어 수만 보면
 * 기준을 넘으니 사다리가 멈춘 것이다.
 *
 * **모르는 단어는 계속 나온다.** 동의서·확약서·증빙서류… 배제 목록을 늘리는
 * 것만으로는 매번 한 발 늦는다. 그래서 반대편에서도 잡는다 — 이름이 공고라고
 * 말하는 파일을 **앞으로 정렬한다.** 실측 186건이 공고 이름 파일을 뒤에 두고
 * 있었다.
 */
export function isNoticeName(name) {
    return NOTICE_NAME_RE.test(name);
}
/**
 * 같은 묶음 안의 읽는 순서. 작을수록 먼저다.
 *
 *   0  「대상 및 내용」 — 지원 내용을 직접 말하는 문서
 *   1  「공고」「내용」「RFP」 — 이름이 스스로 공고·과제 문서라고 말한다
 *   2  나머지
 *
 * 상한(MAX_CANDIDATES)에 걸려 잘리더라도 잘리는 쪽이 뒤여야 한다.
 */
export function noticeNameRank(name) {
    if (PRIMARY_NAME_RE.test(name))
        return 0;
    return isNoticeName(name) ? 1 : 2;
}
/** fetcher 종류 = grant_sources.kind. */
export const SOURCE_KINDS = ['json_api', 'rss', 'html_list', 'file'];
/**
 * 접근 사다리. 숫자가 작을수록 좋은 출처이며, 같은 공고가 여러 출처로 들어올 때
 * 이 값이 canonical 을 결정한다 (spec §5.3). 사다리는 문서가 아니라 정렬 순서다.
 */
export const ACCESS_TIER = {
    api: 1,
    rss: 2,
    file: 3,
    webpage: 4,
    searchOnly: 5,
};
/**
 * 공고 첨부. **금액은 여기 안에 있다** — 요약문에는 37%, 상세 페이지에는 사실상
 * 0%, 첨부 공고문에는 100% 다. 그래서 이 배열이 추출 파이프라인의 입력이다.
 *
 * `kind` 를 나누는 이유: 기업마당이 **공고문(printFileNm)과 첨부서식(fileNm)을
 * 이미 별도 필드로** 준다. 파일명에 "공고/모집"이 들어갔는지로 고르면 90.8% 만
 * 잡히는데 그건 도달 가능성이 아니라 작명 습관이고, 멀쩡한 나머지를 버린다.
 *
 * `ext` 가 처리 경로를 정한다 — `pdf`·`hwpx` 는 바로, `hwp` 는 변환 후,
 * 이미지(`png`·`jpg`)는 OCR 이라 별도 문제다.
 */
export const grantAttachmentSchema = z
    .object({
    name: z.string().min(1),
    url: z.string().min(1),
    /** `notice` = 공고문 본체, `form` = 신청서식·별첨. */
    kind: z.enum(['notice', 'form']).default('form'),
})
    .transform((a) => ({
    ...a,
    /** 파일명에서 뽑은 소문자 확장자. 없으면 null. */
    ext: (a.name.match(/\.([a-zA-Z0-9]{1,5})\s*$/)?.[1] ?? '').toLowerCase() || null,
}));
/** ISO 날짜(YYYY-MM-DD) 또는 null. 출처의 YYYYMMDD 는 preset 이 변환해서 넣는다. */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable();
/**
 * 출처에서 정규화된 공고 1건.
 *
 * sourceUid 가 z.coerce.string() 인 이유: K-Startup 의 pbanc_sn 이 JSON 숫자로
 * 오는데 컬럼은 text 다. 여기서 강제하지 않으면 같은 공고가 tick 마다 다른
 * 키로 보일 수 있다.
 */
/**
 * 출처 텍스트 필드. **정제가 스키마에 붙어 있다.**
 *
 * 파서마다 넣으면 다음 출처를 추가할 때 빠뜨린다 — 실제로 그렇게 빠져서
 * `&apos;` 가 986회, 제목 144건에 그대로 나갔다. 여기 두면 새 preset 이
 * 뭘 하든 정제를 거친다.
 */
const sourceText = z
    .string()
    .nullable()
    .default(null)
    .transform((v) => cleanSourceText(v));
/** 제목은 비어선 안 된다. 정제 후에도 남아야 한다. */
const sourceTitle = z
    .string()
    .min(1)
    .transform((v) => cleanSourceText(v))
    .refine((v) => v !== null, { message: '제목이 정제 후 비었다' });
export const canonicalGrantSchema = z.object({
    sourceUid: z.coerce.string().min(1),
    title: sourceTitle,
    summary: sourceText,
    institution: sourceText,
    executingInstitution: sourceText,
    supportField: sourceText,
    /** 정규 시도 코드만, `REGIONS` 순. 모르는 값은 조용히 떨어진다 — 레코드를 버리지 않는다. */
    supportRegions: z.array(z.string()).default([]).transform((v) => sortRegions(v)),
    /** 전국 공고. true 면 `supportRegions` 는 비어 있어야 한다 (프리셋이 지킨다). */
    nationwide: z.boolean().default(false),
    target: sourceText,
    businessAge: sourceText,
    applyStart: isoDate.default(null),
    applyEnd: isoDate.default(null),
    detailUrl: z.string().nullable().default(null),
    applyUrl: z.string().nullable().default(null),
    /** 출처가 "모집 종료"를 명시했는가 (K-Startup rcrt_prgs_yn = 'N'). */
    sourceClosed: z.boolean().default(false),
    /** 첨부. 금액 추출의 입력이다. */
    attachments: z.array(grantAttachmentSchema).default([]),
    /** 원본 레코드 원형. 재수집 없이 재정규화하기 위해 버리지 않는다. */
    raw: z.record(z.unknown()),
});
/* -------------------------------------------------------------- 읽기 계약 */
/**
 * 목록에서 고를 수 있는 원문 상태.
 *
 * `TEXT_STATUSES` 에 `pending` 을 더한 것이다. DB 에서 「아직 안 봄」은 NULL 인데
 * 쿼리스트링으로는 NULL 을 못 보내므로 이름을 하나 준다.
 */
export const GRANT_TEXT_FILTERS = [...TEXT_STATUSES, 'pending'];
/**
 * 네 기둥 — 공개 목록의 지원 유형 필터 (docs/REFACTOR.md §2).
 *
 *   grant     자금·바우처. 갚지 않는 돈 — 보조금 · 바우처 · 상금 (`grant_fund`)
 *   loan      융자·보증. 갚는 돈과 보증 (`grant_loan`)
 *   facility  시설·입주·공간. 입주 · 사무공간 · 장비 공동활용 (`grant_facility`)
 *   program   프로그램. 그 밖의 모든 것 — 하위 종류(`PROGRAM_KINDS`)로만 구분 (`grant_program`)
 *
 * **저장 테이블(기둥)이지 `support_type` 이 아니다.** 보조금과 바우처를 가르는 것은
 * 회계 형식이지 사용자의 관심이 아니라, 독자에게는 이 넷이 물어볼 수 있는 전부다.
 * 순서가 대표 기둥의 우선순위다 — 카드 첫 칸이 이 순서를 따른다.
 */
export const GRANT_SUPPORT_FILTERS = ['grant', 'loan', 'facility', 'program'];
/**
 * 마감 공고 취급.
 *
 *   exclude  기본. 지금 신청할 수 있는 것만
 *   include  섞어서 본다
 *   only     **마감된 것만** — 지난 공고를 찾을 때
 */
export const GRANT_CLOSED_MODES = ['exclude', 'include', 'only'];
/** 목록 정렬. 기본은 최신순이다 — 이유는 아래 주석. */
export const GRANT_SORTS = ['latest', 'closing', 'amount'];
/**
 * 목록 질의.
 *
 * **`closing`(마감임박순)을 기본값으로 두지 않는다.** 마감일이 없는 공고가
 * 전체 26%, 기업마당만 보면 58%, 중기부 RSS 는 100%다. 마감임박순을 기본으로
 * 걸면 그 절반이 화면에서 사라지고 결과가 K-Startup 쪽으로 쏠린다.
 */
export const grantsListQuerySchema = paginationQuerySchema.extend({
    /** 시도 단위 지역. 전국 공고는 모든 지역 필터에 걸린다. */
    /** 정규 시도 코드만 받는다. `전국` 은 필터가 아니다 — 전국 공고는 어느 지역에서도 나온다. */
    /** 검색어. 제목·기관명 부분일치. 빈 문자열은 「검색 없음」이다 — 400 을 내지 않는다. */
    q: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().trim().max(60).optional()),
    region: z.enum(REGIONS).optional(),
    /** 지원분야 (금융·기술·인력·수출·내수·창업·경영 …). */
    field: z.string().trim().min(1).max(30).optional(),
    /**
     * 지원 유형.
     *
     * **「금액 확인된 것만」을 대신한다.** 그 필터는 「금액이 있나 없나」만 물어서
     * 융자 1건과 프로그램 셋을 가진 공고와 보조금 하나뿐인 공고를 구별하지 못했다.
     * 그리고 절반 이상인 프로그램형 지원은 금액이 없는 게 정상이라, 그 필터를 켜면
     * 실제 지원이 있는 공고가 대량으로 사라졌다.
     *
     * 값은 **저장 테이블**이다 (`grant_subsidy` 등). `support_type` 이 아니라 —
     * `GRANT`·`SUBSIDY` 가 한 테이블이고 `LOAN`·`GUARANTEE` 가 한 테이블인데,
     * 독자에게 그 여섯 갈래를 배우게 할 이유가 없다.
     */
    support: z.enum(GRANT_SUPPORT_FILTERS).optional(),
    /** 프로그램의 하위 종류. `support=program` 일 때만 뜻이 있다 — 프로그램 탭 안의 알약. */
    kind: z.enum(PROGRAM_KINDS).optional(),
    /** 인증·자격 코드. 그 자격을 요구하는 공고만 — `grant_eligibility.code` 로 건다. */
    cert: z.enum(CERTIFICATION_CODES).optional(),
    /**
     * 마감 공고를 어떻게 다룰 것인가.
     *
     * 불리언으로는 **「마감만 보기」를 표현할 수 없다.** 지난 공고를 찾는 것은
     * 실제 용례다 — 지원사업은 대부분 매년 반복되므로 작년 공고가 올해 신청자에게
     * 가장 값진 자료다 (`news.$id.tsx` 가 마감 공고에도 댓글을 열어 두는 이유와 같다).
     */
    closed: z.enum(GRANT_CLOSED_MODES).default('exclude'),
    /** @deprecated `closed` 를 쓴다. 이미 나간 링크를 위해 남긴다. */
    includeClosed: z.coerce.boolean().optional(),
    sort: z.enum(GRANT_SORTS).default('latest'),
});
/* ---------------------------------------------------------------- 원문 뷰어 */
/** 브라우저 PDF 뷰어로 보여줄 수 있거나(pdf) rhwp 로 PDF 가 되는(hwp 계열) 확장자. */
const VIEWABLE_EXTS = new Set(['pdf', 'hwp', 'hwpx', 'hml', 'jpg', 'jpeg', 'png']);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png']);
/** 원문이 그림 한 장인 공고(K-Startup 의 포스터 jpg 등). 뷰어 대신 <img> 로 보여준다. */
export const isImageExt = (ext) => ext !== null && IMAGE_EXTS.has(ext);
/** 같은 순위면 pdf(변환 없음) → hwp 계열(변환) → 그림 순. */
const viewRank = (ext) => (ext === 'pdf' ? 0 : IMAGE_EXTS.has(ext) ? 2 : 1);
/**
 * 상세의 「공고문 원문」 탭에 띄울 첨부 하나.
 *
 * 서식은 제외하고, 이름이 공고라고 말하는 파일을 앞에 둔다 (`noticeNameRank`).
 * 같은 순위면 **pdf 가 먼저다** — 변환 없이 바로 보여줄 수 있다. 같은 공고문이
 * pdf·hwpx 로 나란히 붙는 일이 흔하다(군포시 우수기업: 둘 다 「…공고문」).
 *
 * API(변환·전송)와 웹(탭을 그릴지)이 **같은 함수**를 쓴다. 갈라지면 웹이 탭을
 * 그렸는데 API 가 404 를 주는 화면이 나온다.
 */
export function noticeFileCandidates(attachments) {
    return attachments
        .filter((a) => a.ext !== null && VIEWABLE_EXTS.has(a.ext) && !isFormName(a.name))
        .map((a, i) => ({ a, i, rank: noticeNameRank(a.name), pdf: viewRank(a.ext) }))
        .sort((x, y) => x.rank - y.rank || x.pdf - y.pdf || x.i - y.i)
        .map((x) => x.a);
}
/**
 * 첫 후보. 웹이 「원문 탭을 그릴까」를 정할 때 쓴다. API 는 `noticeFileCandidates` 를
 * 순서대로 시도한다 — 첫 후보가 빈 파일(기업마당 fileSn=0, 실측 83527d68)이면 다음 것.
 */
export function pickNoticeFile(attachments) {
    return noticeFileCandidates(attachments)[0] ?? null;
}
//# sourceMappingURL=grants.js.map